import React, {useEffect} from 'react'
import {Controller, useForm} from "react-hook-form";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel, MenuItem, Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import {Dayjs} from 'dayjs';
import LMTable from "../../Tables/LMTable";
import SubDeviceDialogButton, {SubDeviceFormValues} from "../../dialogs/SubDeviceDialogButton";
import 'dayjs/locale/en-gb';
import {FormIds, subDeviceColumns} from "../../../constants";
import LMDatePicker from "../../subcomponents/LMDatePicker";
import {useTranslation} from "react-i18next";
import {useUserRole} from "../../../utils/UserRoleContext";

export interface InPaceProductFormValues {
  deviceName: string
  serialNr: string
  deviceType: string
  manufacturer: string
  softwareVn: string
  licenceStart: Dayjs | null
  licenceEnd: Dayjs | null
  dateOfActivation: Dayjs | null
  additionalNotes: string
  subDevice: SubDeviceFormValues[]
  soldToBiotronik: boolean
}

export interface InPaceProductFormProps {
  onSubmit: (data: InPaceProductFormValues) => void,
  /** This function updates the dirty state in the parents component */
  updateDirty: (dirty: boolean) => void,
  defaultValues: InPaceProductFormValues
}

export default function InPaceProductForm({onSubmit, defaultValues, updateDirty}: Readonly<InPaceProductFormProps>) {

  const form = useForm<InPaceProductFormValues>({
    defaultValues
  })
  const {register, handleSubmit, formState, getValues, setValue, control, watch} = form
  const {errors} = formState

  const [selectedSubDevice, setSelectedSubDevice] = React.useState<string[]>([]);
  const [subDevices, setSubDevices] = React.useState(getValues().subDevice)

  const watchedDeviceType = watch('deviceType', getValues().deviceType) // Triggers rerender on changed values in select

  const {t} = useTranslation()
  const {isAdmin} = useUserRole();

  useEffect(() => {
    updateDirty(form.formState.isDirty)
  }, [form.formState.isDirty])

  function handleSubDeviceSelectionChange(selectionModel: string[]) {
    // LMTable enforces single-selection (radio behaviour), so store the model as-is.
    setSelectedSubDevice(selectionModel)
  }

  function getEmptySubDeviceForm(): SubDeviceFormValues {
    return {
      subDeviceNo: "",
      einstellung: "",
      subDeviceSoftwareVn: ""
    }
  }

  function findSelectedSubDevice(): SubDeviceFormValues {
    const subDeviceCopy = [...getValues().subDevice]
    const item = subDeviceCopy.find((elem) => (elem.subDeviceNo === selectedSubDevice[0]))
    if (item === undefined) {
      throw Error("Subdevice could not be found")
    }
    return item
  }

  function addSubDevice(formValues: SubDeviceFormValues) {
    const subDeviceCopy = [...getValues().subDevice]
    subDeviceCopy.push(formValues)
    setValue("subDevice", subDeviceCopy)
    setSubDevices(subDeviceCopy)
  }

  function updateSubDevice(formValues: SubDeviceFormValues) {
    const subDeviceCopy = [...getValues().subDevice]
    for (let i = 0; i < subDeviceCopy.length; i++) {
      if (subDeviceCopy[i].subDeviceNo === selectedSubDevice[0]) {
        subDeviceCopy[i] = formValues
      }
    }
    setValue("subDevice", subDeviceCopy)
    setSubDevices(subDeviceCopy)
  }

  function deleteSubDevice() {
    const subDeviceCopy = [...getValues().subDevice]
    const newSubDevices = subDeviceCopy.filter(subDevice => subDevice.subDeviceNo !== selectedSubDevice[0])
    setValue("subDevice", newSubDevices)
    updateDirty(true)
    setSelectedSubDevice([])
    setSubDevices(newSubDevices)
  }

  function subDeviceButtonDisabled() {
    return selectedSubDevice.length !== 1
  }

  /**
   * Checks if subDeviceNo can be set, because this is the identifier of the subDevice
   * @param id
   */
  function validateSubDeviceId(id: string) {
    const subDeviceCopy = [...getValues().subDevice]

    for (const subDevice of subDeviceCopy) {
      if (subDevice.subDeviceNo === id) {
        return false
      }
    }
    return true
  }

  return (
    <form id={FormIds.updateInPace} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            gap: 2,
            marginTop: 2,
            paddingTop: 2,
            paddingLeft: 2,
            paddingRight: 2,
            flexFlow: 'row wrap'
          }}>
        {/* Left form element*/}
        <Box sx={{display: 'flex', flexWrap: 'unset', flexDirection: 'column', width: '50%'}}>
          <div style={{
            display: 'flex',
            flexWrap: 'unset',
            flexDirection: 'column',
            marginLeft: '1rem',
            marginRight: '2rem',
            marginBottom: '1rem'
          }}>
            <TextField
              id={"serialNr"}
              variant="standard"
              label={t('objects.products.inpace.serialNr')}
              {...register("serialNr", {
                required: t('objects.products.inpace.requiredMessage', {obj: t('objects.products.inpace.serialNr')})
              })}
              required
              sx={{marginTop: 1}}
              error={!!errors.serialNr}
              helperText={errors.serialNr?.message}
              disabled={!isAdmin}
            />
            <TextField
              sx={{marginTop: 1}}
              id={"deviceName"}
              variant="standard"
              required
              label={t('objects.products.inpace.deviceName')}
              {...register("deviceName", {
                required: t('objects.products.inpace.requiredMessage', {obj: t('objects.products.inpace.deviceName')})
              })}
              error={!!errors.deviceName}
              helperText={errors.deviceName?.message}
              disabled={!isAdmin}
            />
            <TextField
              sx={{marginTop: 1}}
              id={"manufacturer"}
              variant="standard"
              disabled
              label={t('objects.products.inpace.manufacturer')}
              {...register("manufacturer", {
                required: t('objects.products.inpace.requiredMessage', {obj: t('objects.products.inpace.manufacturer')})
              })}
              error={!!errors.manufacturer}
              helperText={errors.manufacturer?.message}
            />
          </div>
          <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between'}}>
            <Box sx={{
                  height: '15rem', display: 'flex', marginBottom: '1rem',
                  alignItems: 'flex', justifyContent: 'space-evenly', flexWrap: 'wrap'
                }}>
              <TextField
                id={"softwareVn"}
                variant="standard"
                label="Software Vn"
                {...register("softwareVn")}
                disabled={!isAdmin}
              />

              <LMDatePicker
                value={getValues().dateOfActivation}
                required={true}
                label={t('objects.products.inpace.dateOfActivation')}
                error={!!errors.dateOfActivation}
                errorHelperText={errors.dateOfActivation?.message}
                formRegisterObject={{
                  ...register('dateOfActivation', {
                    required: t('objects.products.inpace.requiredMessage', {
                      obj: t('objects.products.inpace.dateOfActivation')
                    })
                  }),
                  disabled: !isAdmin,
                }}
                onChange={value => setValue('dateOfActivation', value, {shouldDirty: true})}
              />
              <LMDatePicker
                value={getValues().licenceStart}
                required={true}
                label={t('objects.products.inpace.licenseStart')}
                error={!!errors.licenceStart}
                errorHelperText={errors.licenceStart?.message}
                formRegisterObject={{
                  ...register('licenceStart',
                    {required: t('objects.products.inpace.requiredMessage', {obj: t('objects.products.inpace.licenseStart')}
                      )}
                  ),
                  disabled: !isAdmin,
                }}
                onChange={value => setValue('licenceStart', value, {shouldDirty: true})}
              />
              <LMDatePicker
                value={getValues().licenceEnd}
                required={true}
                label={t('objects.products.inpace.licenceEnd')}
                error={!!errors.licenceEnd}
                errorHelperText={errors.licenceEnd?.message}
                formRegisterObject={{
                  ...register('licenceEnd',
                    {required: t('objects.products.inpace.requiredMessage', {obj: t('objects.products.inpace.licenceEnd')})}
                  ),
                  disabled: !isAdmin,
                }}
                onChange={value => setValue('licenceEnd', value, {shouldDirty: true})}
              />

              <Controller
                name={"soldToBiotronik"}
                control={control}
                render={({field: {onChange, value}}) => (
                  <FormControlLabel
                    disabled={!isAdmin}
                    control={<Checkbox checked={value} {...register("soldToBiotronik")}/>}
                    label={t('objects.products.inpace.soldToBiotronik')}
                  />
                )}
              />

              <FormControl id={'form_control_deviceType'} variant={'standard'} disabled={!isAdmin}>
                <InputLabel id={'form_deviceType_label'}>{t('objects.products.inpace.deviceType')}</InputLabel>
                <Select
                  id={"form_deviceType_selector"}
                  sx={{width: "250px"}}
                  labelId={'form_deviceType_label'}
                  value={watchedDeviceType}
                  onChange={event => setValue('deviceType', event.target.value as string, {shouldDirty: true})}
                >
                  <MenuItem sx={{height: "2rem"}} value={""}></MenuItem>
                  <MenuItem value={"hub"}>Hub</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField sx={{width: '100%', mr: '2rem'}}
                       label={'Notizen'}
                       multiline size={'medium'} {...register("additionalNotes")}
                       disabled={!isAdmin}
            />
          </Box>
        </Box>

        {/* Right form element*/}
        <Box sx={{width: '50%', display: 'flex', flexDirection: 'column', gap: 2}}>
          <Box sx={{display: 'grid'}}>
            <Typography>{t('objects.products.inpace.subDevices')}</Typography>
            <LMTable
              rows={subDevices}
              columns={subDeviceColumns}
              getRowId={(row) => row.subDeviceNo}
              selectionMode="single"
              selectionModel={selectedSubDevice}
              selectionHandler={handleSubDeviceSelectionChange}
              pageSizeOptions={[5, 10, 50]}
              maxHeight={300}
            />
            <Stack spacing={2} direction={"row"}>
              <Button
                disabled={subDeviceButtonDisabled() || !isAdmin}
                onClick={deleteSubDevice}
              >
                {t('activities.delete')}
              </Button>
              <SubDeviceDialogButton
                type="update"
                disabled={subDeviceButtonDisabled() || !isAdmin}
                subDeviceDefaultValues={
                  selectedSubDevice.length === 1 ? findSelectedSubDevice() : getEmptySubDeviceForm()
                }
                submitOperation={updateSubDevice}
                updateDirty={updateDirty}
                validateSubDeviceId={validateSubDeviceId}
              />
              <SubDeviceDialogButton
                type="add"
                subDeviceDefaultValues={getEmptySubDeviceForm()}
                submitOperation={addSubDevice}
                updateDirty={updateDirty}
                validateSubDeviceId={validateSubDeviceId}
                disabled={!isAdmin}
              />
            </Stack>
          </Box>
        </Box>
      </Box>
    </form>
  )
}
